import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';
import {
  Server, Database, FileCode, ArrowLeft, CheckCircle2, AlertTriangle,
  Copy, Terminal, BookOpen, Settings, ShieldCheck, HardDrive,
  ChevronRight, ExternalLink, Wifi, WifiOff, RefreshCw, Info,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { checkPhpBackend } from '@/lib/phpApi';

const SETUP_STEPS = [
  {
    id: 'requirements',
    title: 'Server Requirements',
    icon: Server,
    color: '#22d3ee',
    content: [
      { label: 'PHP', value: '8.1 or higher', note: 'With PDO, JSON, and mbstring extensions' },
      { label: 'MySQL', value: '5.7 or 8.0+', note: 'Or MariaDB 10.5+' },
      { label: 'Web Server', value: 'Apache / Nginx', note: 'With mod_rewrite or equivalent' },
      { label: 'Storage', value: '1 GB minimum', note: 'For application files and uploads' },
    ],
  },
  {
    id: 'database',
    title: 'Create MySQL Database',
    icon: Database,
    color: 'var(--rb-brand)',
    code: `CREATE DATABASE rb_wastewater CHARACTER SET utf8mb4 COLLATE utf8mb4_unicode_ci;

CREATE USER 'rb_user'@'localhost' IDENTIFIED BY 'your_secure_password';
GRANT ALL PRIVILEGES ON rb_wastewater.* TO 'rb_user'@'localhost';
FLUSH PRIVILEGES;`,
    note: 'Run these commands in MySQL CLI, phpMyAdmin, or your database management tool. Replace the password with a strong, unique value.',
  },
  {
    id: 'schema',
    title: 'Import Database Schema',
    icon: FileCode,
    color: '#a855f7',
    code: `USE rb_wastewater;

-- Users table
CREATE TABLE users (
  id INT AUTO_INCREMENT PRIMARY KEY,
  username VARCHAR(100) UNIQUE NOT NULL,
  password_hash VARCHAR(255) NOT NULL,
  name VARCHAR(200),
  first_name VARCHAR(100),
  last_name VARCHAR(100),
  email VARCHAR(200),
  role ENUM('user','admin') DEFAULT 'user',
  country VARCHAR(10) DEFAULT 'GB',
  company_name VARCHAR(200),
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
);

-- Assessment results table
CREATE TABLE assessment_results (
  id INT AUTO_INCREMENT PRIMARY KEY,
  user_id INT NOT NULL,
  report_number VARCHAR(50),
  sample_name VARCHAR(255),
  params JSON,
  blended JSON,
  limits JSON,
  result JSON,
  dosing_config JSON,
  latitude DECIMAL(10,8),
  longitude DECIMAL(11,8),
  city VARCHAR(100),
  country VARCHAR(100),
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  FOREIGN KEY (user_id) REFERENCES users(id) ON DELETE CASCADE
);

-- Sample reports table
CREATE TABLE sample_reports (
  id INT AUTO_INCREMENT PRIMARY KEY,
  user_id INT NOT NULL,
  report_number VARCHAR(50),
  report_title VARCHAR(255),
  samples JSON,
  standard VARCHAR(50),
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  FOREIGN KEY (user_id) REFERENCES users(id) ON DELETE CASCADE
);

-- Customers table
CREATE TABLE customers (
  id INT AUTO_INCREMENT PRIMARY KEY,
  user_id INT,
  name VARCHAR(255) NOT NULL,
  company VARCHAR(255),
  email VARCHAR(200),
  phone VARCHAR(50),
  address TEXT,
  standard VARCHAR(50) DEFAULT 'UK_EA',
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Samples table
CREATE TABLE water_samples (
  id INT AUTO_INCREMENT PRIMARY KEY,
  customer_id INT NOT NULL,
  sample_name VARCHAR(255),
  params JSON,
  standard VARCHAR(50),
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  FOREIGN KEY (customer_id) REFERENCES customers(id) ON DELETE CASCADE
);`,
    note: 'Copy and paste this SQL into your MySQL client. This creates all tables needed for the application.',
  },
  {
    id: 'php-api',
    title: 'PHP API Configuration',
    icon: FileCode,
    color: '#f59e0b',
    code: `<?php
// api/config.php

define('DB_HOST', 'localhost');
define('DB_NAME', 'rb_wastewater');
define('DB_USER', 'rb_user');
define('DB_PASS', 'your_secure_password');
define('DB_CHARSET', 'utf8mb4');

// JWT secret for authentication
define('JWT_SECRET', 'your_random_secret_key_here_change_this');

// CORS headers for frontend communication
header('Access-Control-Allow-Origin: *');
header('Access-Control-Allow-Methods: GET, POST, PUT, DELETE, OPTIONS');
header('Access-Control-Allow-Headers: Content-Type, Authorization');

// Database connection
function getDb() {
  static $pdo = null;
  if ($pdo === null) {
    try {
      $dsn = "mysql:host=" . DB_HOST . ";dbname=" . DB_NAME . ";charset=" . DB_CHARSET;
      $pdo = new PDO($dsn, DB_USER, DB_PASS);
      $pdo->setAttribute(PDO::ATTR_ERRMODE, PDO::ERRMODE_EXCEPTION);
      $pdo->setAttribute(PDO::ATTR_DEFAULT_FETCH_MODE, PDO::FETCH_ASSOC);
    } catch (PDOException $e) {
      http_response_code(500);
      echo json_encode(['error' => 'Database connection failed']);
      exit;
    }
  }
  return $pdo;
}`,
    note: 'Create an api/ folder in your web root. Save this as config.php. Update the credentials to match your database.',
  },
  {
    id: 'auth-endpoint',
    title: 'Authentication Endpoints',
    icon: ShieldCheck,
    color: '#34d399',
    code: `<?php
// api/auth/login.php

require_once '../config.php';

$data = json_decode(file_get_contents('php://input'), true);
$username = $data['username'] ?? '';
$password = $data['password'] ?? '';

$db = getDb();
$stmt = $db->prepare("SELECT * FROM users WHERE username = ?");
$stmt->execute([$username]);
$user = $stmt->fetch();

if (!$user || !password_verify($password, $user['password_hash'])) {
  http_response_code(401);
  echo json_encode(['message' => 'Invalid credentials']);
  exit;
}

// Generate JWT token
$header = json_encode(['typ' => 'JWT', 'alg' => 'HS256']);
$time = time();
$payload = json_encode([
  'iss' => 'rb-app',
  'iat' => $time,
  'exp' => $time + (7 * 24 * 3600), // 7 days
  'sub' => $user['id'],
  'username' => $user['username'],
  'role' => $user['role']
]);

$base64Header = str_replace(['+', '/', '='], ['-', '_', ''], base64_encode($header));
$base64Payload = str_replace(['+', '/', '='], ['-', '_', ''], base64_encode($payload));
$signature = hash_hmac('sha256', $base64Header . "." . $base64Payload, JWT_SECRET, true);
$base64Signature = str_replace(['+', '/', '='], ['-', '_', ''], base64_encode($signature));
$token = $base64Header . "." . $base64Payload . "." . $base64Signature;

echo json_encode([
  'token' => $token,
  'user' => [
    'id' => $user['id'],
    'username' => $user['username'],
    'name' => $user['name'],
    'firstName' => $user['first_name'],
    'lastName' => $user['last_name'],
    'email' => $user['email'],
    'role' => $user['role'],
    'country' => $user['country'],
    'companyName' => $user['company_name']
  ]
]);`,
    note: 'Create the auth/ folder inside api/. This is the login endpoint. Create similar endpoints for register.php, me.php, and logout.php.',
  },
  {
    id: 'trpc-bridge',
    title: 'tRPC Router Bridge',
    icon: RefreshCw,
    color: '#f97316',
    code: `<?php
// api/trpc/index.php — Main tRPC request handler

require_once __DIR__ . '/../config.php';

$input = file_get_contents('php://input');
$requests = json_decode($input, true);

if (!$requests || !is_array($requests)) {
  http_response_code(400);
  echo json_encode(['error' => 'Invalid request']);
  exit;
}

$responses = [];
foreach ($requests as $req) {
  $path = $req['path'] ?? '';
  $input = $req['input'] ?? new stdClass();
  
  $result = null;
  $error = null;
  
  try {
    switch ($path) {
      case 'results.save':
        $result = handleSaveResult($input);
        break;
      case 'results.myResults':
        $result = handleMyResults();
        break;
      case 'sampleReports.myReports':
        $result = handleMyReports();
        break;
      case 'sampleReports.create':
        $result = handleCreateReport($input);
        break;
      case 'sampleReports.delete':
        $result = handleDeleteReport($input);
        break;
      case 'auth.me':
        $result = handleAuthMe();
        break;
      default:
        throw new Exception('Unknown procedure: ' . $path);
    }
  } catch (Exception $e) {
    $error = [
      'message' => $e->getMessage(),
      'code' => 'INTERNAL_SERVER_ERROR'
    ];
  }
  
  if ($error) {
    $responses[] = ['error' => ['json' => $error]];
  } else {
    $responses[] = ['result' => ['data' => json_decode(json_encode($result), true)]];
  }
}

header('Content-Type: application/json');
echo json_encode($responses);

// ─── Handler functions ───
function handleSaveResult($input) {
  $userId = getCurrentUserId();
  $db = getDb();
  $reportNum = 'RB-' . date('Ymd-His') . '-' . substr(uniqid(), -4);
  
  $stmt = $db->prepare("INSERT INTO assessment_results 
    (user_id, report_number, sample_name, params, blended, limits, result, dosing_config, latitude, longitude, city, country)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)");
  $stmt->execute([
    $userId, $reportNum, $input->sampleName ?? 'Untitled',
    json_encode($input->params ?? []), json_encode($input->blended ?? []),
    json_encode($input->limits ?? []), json_encode($input->result ?? []),
    json_encode($input->dosingConfig ?? []),
    $input->latitude ?? null, $input->longitude ?? null,
    $input->city ?? null, $input->country ?? null
  ]);
  
  return ['id' => $db->lastInsertId(), 'reportNumber' => $reportNum];
}

function handleMyResults() {
  $userId = getCurrentUserId();
  $db = getDb();
  $stmt = $db->prepare("SELECT * FROM assessment_results WHERE user_id = ? ORDER BY created_at DESC");
  $stmt->execute([$userId]);
  return $stmt->fetchAll();
}

function getCurrentUserId() {
  // Parse JWT from Authorization header
  $auth = $_SERVER['HTTP_AUTHORIZATION'] ?? '';
  if (!preg_match('/Bearer (.*)/', $auth, $matches)) {
    throw new Exception('Not authenticated');
  }
  // Verify JWT and extract user ID
  // ... JWT verification logic here
  return 1; // Replace with actual user ID from JWT
}`,
    note: 'This is the main tRPC request handler. All tRPC calls from the frontend come here. Route all /api/trpc requests to this file.',
  },
  {
    id: 'webserver',
    title: 'Web Server Configuration',
    icon: HardDrive,
    color: '#3b82f6',
    code: `# Apache (.htaccess in web root)
RewriteEngine On
RewriteCond %{REQUEST_FILENAME} !-f
RewriteCond %{REQUEST_FILENAME} !-d
RewriteRule ^api/(.*)$ api/$1 [L]

# Route all /api/trpc to api/trpc/index.php
RewriteRule ^api/trpc$ api/trpc/index.php [L]
RewriteRule ^api/trpc/(.*)$ api/trpc/index.php [L]

# Protect sensitive files
<Files "config.php">
  Order allow,deny
  Deny from all
</Files>`,
    note: 'Place this .htaccess in your web root. For Nginx, use equivalent location blocks in your server config.',
  },
  {
    id: 'env-var',
    title: 'Connect Frontend to Backend',
    icon: Wifi,
    color: '#22c55e',
    content: [
      { label: 'Step 1', value: 'Host PHP files', note: 'Upload the api/ folder to your web server (e.g., /var/www/html/api/)' },
      { label: 'Step 2', value: 'Test the API', note: 'Visit https://yourserver.com/api/ping in a browser. You should get {"status":"ok"}' },
      { label: 'Step 3', value: 'Set environment variable', note: 'Create a .env file or set VITE_API_URL=https://yourserver.com/api' },
      { label: 'Step 4', value: 'Rebuild & deploy', note: 'npm run build and upload the dist/ folder to your web root' },
    ],
    code: `# .env file in project root
VITE_API_URL=https://yourserver.com/api

# Then rebuild the app
cd /path/to/project
npm run build

# Deploy dist/ to your web server
rsync -av dist/ user@yourserver.com:/var/www/html/`,
  },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          toast.success('Copied to clipboard');
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-rb-brand transition-colors px-2 py-1 rounded border border-slate-700 hover:border-rb-brand/30"
    >
      {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function BackendSetupPage() {
  const navigate = useNavigate();
  const [openStep, setOpenStep] = useState<string>('requirements');
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [backendDetails, setBackendDetails] = useState<string>('');

  useEffect(() => {
    checkPhpBackend().then((result) => {
      if (result.available) {
        setBackendStatus('online');
      } else {
        setBackendStatus('offline');
        const details = [];
        if (!result.pingOk) details.push(result.pingError || 'Ping failed');
        if (!result.trpcOk) details.push(result.trpcError || 'Auth check failed');
        setBackendDetails(details.join(' / '));
      }
    });
  }, []);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <PageHeader
        icon={Server}
        title="Backend Setup Guide"
        subtitle="Configure PHP + MySQL for data persistence and multi-user support"
        actions={
          <button onClick={() => navigate('/')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold bg-slate-800/80 border border-slate-800/60 text-slate-300 hover:bg-slate-700 hover:border-slate-600 transition-all">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        }
      />

      {/* Status Banner */}
      {backendStatus === 'online' ? (
        <div className="rb-card p-4 border border-emerald-500/30 bg-emerald-500/5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-emerald-400 mb-1">Backend Connected</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                Your PHP + MySQL backend is online and reachable. All data is being persisted to the database.
                You can use this page to review the server configuration or troubleshoot if issues arise.
              </p>
            </div>
          </div>
        </div>
      ) : backendStatus === 'offline' ? (
        <div className="rb-card p-4 border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-400 mb-1">Currently Running in Local Mode</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                Without a backend, all data is stored in your browser's localStorage. This is fine for single-user evaluation,
                but for production use with multiple users and persistent data storage, you need to set up a PHP + MySQL backend.
                {backendDetails && <span className="block mt-1 text-amber-300/70">Diagnostic: {backendDetails}</span>}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rb-card p-4 border border-slate-700">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-700/50 border border-slate-600 flex items-center justify-center shrink-0 animate-pulse">
              <RefreshCw className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-300 mb-1">Checking Backend Status...</p>
              <p className="text-xs text-slate-400 leading-relaxed">Running diagnostics on your server configuration.</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Nav */}
      <div className="flex flex-wrap gap-2">
        {SETUP_STEPS.map(step => (
          <button
            key={step.id}
            onClick={() => setOpenStep(openStep === step.id ? '' : step.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all border ${
              openStep === step.id
                ? 'bg-rb-brand/15 border-rb-brand/40 text-rb-brand'
                : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
            }`}
          >
            <step.icon className="w-3.5 h-3.5" />
            {step.title}
          </button>
        ))}
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {SETUP_STEPS.map((step, index) => {
          const isOpen = openStep === step.id;
          const StepIcon = step.icon;
          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`rb-card overflow-hidden transition-all ${isOpen ? 'border-slate-600' : 'border-slate-800/50'}`}
            >
              <button
                onClick={() => setOpenStep(isOpen ? '' : step.id)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-800/30 transition-all group"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${step.color}15`, border: `1px solid ${step.color}30` }}>
                  <StepIcon className="w-5 h-5" style={{ color: step.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">Step {index + 1}: {step.title}</span>
                    {step.id === 'requirements' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </div>
                  <p className="text-xs text-slate-500">
                    {step.id === 'requirements' && 'What you need before starting'}
                    {step.id === 'database' && 'Create the MySQL database and user'}
                    {step.id === 'schema' && 'Import all required tables'}
                    {step.id === 'php-api' && 'Configure the PHP API connection'}
                    {step.id === 'auth-endpoint' && 'Set up user login/register'}
                    {step.id === 'trpc-bridge' && 'Handle tRPC requests from the frontend'}
                    {step.id === 'webserver' && 'Configure Apache or Nginx'}
                    {step.id === 'env-var' && 'Link frontend to your backend'}
                  </p>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>

              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 border-t border-slate-800 pt-4 space-y-3">
                    {step.content && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {step.content.map((item, i) => (
                          <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-slate-500 uppercase font-bold">{item.label}</span>
                            </div>
                            <p className="text-sm font-bold text-white">{item.value}</p>
                            <p className="text-[10px] text-slate-500">{item.note}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {step.code && (
                      <div className="relative">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1">
                            <Terminal className="w-3 h-3" /> Code
                          </span>
                          <CopyButton text={step.code} />
                        </div>
                        <pre className="p-4 rounded-xl bg-slate-950 border border-slate-800 overflow-x-auto text-xs leading-relaxed">
                          <code className="text-slate-300">{step.code}</code>
                        </pre>
                      </div>
                    )}
                    {step.note && (
                      <div className="flex items-start gap-2 text-xs text-slate-400 bg-slate-800/30 p-3 rounded-lg border border-slate-800/50">
                        <Info className="w-4 h-4 text-rb-brand shrink-0 mt-0.5" />
                        <span>{step.note}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Summary Card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="rb-card p-6 border border-rb-brand/20 bg-rb-brand/5">
        <h3 className="text-sm font-bold text-rb-brand mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> After Setup Checklist
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            'Database is created and tables are imported',
            'config.php has correct database credentials',
            'Auth endpoints (login, register, me) are working',
            'tRPC router handles all frontend requests',
            'Web server routes /api/trpc to the handler',
            'VITE_API_URL is set and app is rebuilt',
            'CORS headers allow your frontend origin',
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
              <span className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0 text-[10px] font-bold text-slate-500">{i + 1}</span>
              {item}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Troubleshooting */}
      <div className="rb-card p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" /> Troubleshooting
        </h3>
        <div className="space-y-2">
          {[
            { q: 'Connection refused / 404 errors', a: 'Check that your web server is running and the api/ folder is in the correct location. Verify .htaccess or Nginx config is active.' },
            { q: 'CORS errors in browser console', a: 'Ensure the CORS headers in config.php are being sent. Some servers strip headers — check your web server config.' },
            { q: 'Database connection failed', a: 'Verify DB_HOST, DB_NAME, DB_USER, DB_PASS in config.php. Test the connection with a simple PHP script using PDO.' },
            { q: 'JWT token not working', a: 'Make sure JWT_SECRET is set and consistent. Check that the Authorization: Bearer header is being sent with requests.' },
          ].map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
              <p className="text-xs font-bold text-white mb-1">{item.q}</p>
              <p className="text-[11px] text-slate-400">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
